import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Search, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { formatCurrencyDecimals } from '../../utils/formatters';
import {
  addHotelsToLaunchedCampaign,
  type AddCampaignHotelResult,
} from '../../utils/revenueCollectionLaunch';
import { isCampaignHotelCancelled } from '../../utils/revenueCampaignHotelStatus';

interface HotelRow {
  id: number;
  title: string;
  invoicing_contact: string | null;
  estimated_revenue: number;
  groups_count: number;
  wasCancelled: boolean;
}

function compareHotelTitle(a: string | null | undefined, b: string | null | undefined): number {
  return (a ?? '')
    .trim()
    .localeCompare((b ?? '').trim(), 'en', { sensitivity: 'base', numeric: true });
}

function summarizeAddResults(results: AddCampaignHotelResult[]): string {
  const sent = results.filter((r) => r.action === 'sent').length;
  const skipped = results.filter((r) => r.action === 'skipped_no_contact').length;
  const already = results.filter((r) => r.action === 'already_in_campaign').length;
  const reactivated = results.filter((r) => r.action === 'reactivated').length;
  const blocked = results.filter((r) => r.action === 'blocked_completed').length;
  const errors = results.filter((r) => r.action === 'error');
  const parts: string[] = [];
  if (sent > 0) parts.push(`${sent} invitation(s) sent`);
  if (reactivated > 0) parts.push(`${reactivated} restored without re-email (already submitted)`);
  if (skipped > 0) parts.push(`${skipped} skipped (no invoicing contact)`);
  if (already > 0) parts.push(`${already} already in campaign`);
  if (blocked > 0) parts.push(`${blocked} blocked (collection already completed)`);
  if (errors.length > 0) parts.push(`${errors.length} error(s)`);
  return parts.length > 0 ? parts.join(' · ') : 'No changes';
}

export function AddHotelsToLaunchedCampaignModal({
  open,
  onClose,
  campaignId,
  periodMonth,
  periodYear,
  campaignHotels,
  onSuccess,
}: Readonly<{
  open: boolean;
  onClose: () => void;
  campaignId: string;
  periodMonth: number;
  periodYear: number;
  campaignHotels: { hotel_id: number; status: string }[];
  onSuccess: () => void | Promise<void>;
}>) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultSummary, setResultSummary] = useState<string | null>(null);
  const [hotels, setHotels] = useState<HotelRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [hotelSearchQuery, setHotelSearchQuery] = useState('');
  const [missingContactFilter, setMissingContactFilter] = useState(false);

  const activeHotelIds = useMemo(
    () =>
      new Set(
        campaignHotels.filter((ch) => !isCampaignHotelCancelled(ch.status)).map((ch) => ch.hotel_id),
      ),
    [campaignHotels],
  );

  const cancelledHotelIds = useMemo(
    () =>
      new Set(
        campaignHotels.filter((ch) => isCampaignHotelCancelled(ch.status)).map((ch) => ch.hotel_id),
      ),
    [campaignHotels],
  );

  const loadHotels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: groupData, error: groupErr } = await supabase
        .from('mv_group_enriched_summary')
        .select('hotel_id, estimated_revenue')
        .eq('arrival_month', periodMonth)
        .eq('arrival_year', periodYear)
        .in('status', ['Booked', 'reserved'])
        .gt('estimated_revenue', 0);

      if (groupErr) throw groupErr;

      const byHotel = new Map<number, { revenue: number; count: number }>();
      (groupData || []).forEach((r: { hotel_id: number | null; estimated_revenue: string | number }) => {
        const hid = r.hotel_id;
        if (hid == null) return;
        const rev = Number(r.estimated_revenue) || 0;
        const cur = byHotel.get(hid) || { revenue: 0, count: 0 };
        byHotel.set(hid, { revenue: cur.revenue + rev, count: cur.count + 1 });
      });

      const mvHotelIds = Array.from(byHotel.keys()).filter((id) => !activeHotelIds.has(id));
      const cancelledOnlyIds = Array.from(cancelledHotelIds).filter((id) => !byHotel.has(id));
      const allIds = [...new Set([...mvHotelIds, ...cancelledOnlyIds])];

      if (allIds.length === 0) {
        setHotels([]);
        return;
      }

      const { data: hotelData, error: hotelErr } = await supabase
        .from('hotels')
        .select('id, title, invoicing_contact')
        .in('id', allIds);

      if (hotelErr) throw hotelErr;

      const list: HotelRow[] = (hotelData || []).map(
        (h: { id: number; title: string; invoicing_contact: string | null }) => {
          const agg = byHotel.get(h.id) || { revenue: 0, count: 0 };
          return {
            id: h.id,
            title: h.title,
            invoicing_contact: h.invoicing_contact,
            estimated_revenue: agg.revenue,
            groups_count: agg.count,
            wasCancelled: cancelledHotelIds.has(h.id),
          };
        },
      );
      setHotels(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load hotels');
      setHotels([]);
    } finally {
      setLoading(false);
    }
  }, [periodMonth, periodYear, activeHotelIds, cancelledHotelIds]);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(new Set());
    setHotelSearchQuery('');
    setMissingContactFilter(false);
    setResultSummary(null);
    void loadHotels();
  }, [open, loadHotels]);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el || !open) return;
    if (!el.open) el.showModal();
    return () => {
      if (el.open) el.close();
    };
  }, [open]);

  const hotelsSorted = useMemo(
    () => [...hotels].sort((a, b) => compareHotelTitle(a.title, b.title)),
    [hotels],
  );

  const hotelsFiltered = useMemo(() => {
    let list = hotelsSorted;
    if (missingContactFilter) {
      list = list.filter((h) => !h.invoicing_contact?.trim());
    }
    const q = hotelSearchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((h) => (h.title || '').toLowerCase().includes(q));
  }, [hotelsSorted, hotelSearchQuery, missingContactFilter]);

  const hotelsWithoutEmail = hotels.filter((h) => !h.invoicing_contact?.trim());
  const selectedWithoutContact = hotels.filter(
    (h) => selectedIds.has(h.id) && !h.invoicing_contact?.trim(),
  );

  const toggleHotel = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleClose = () => {
    if (submitting) return;
    dialogRef.current?.close();
    onClose();
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) {
      setError('Select at least one hotel');
      return;
    }
    if (selectedWithoutContact.length > 0) {
      const ok = window.confirm(
        `${selectedWithoutContact.length} selected hotel(s) have no invoicing contact. They will be added but invitations will not be sent until a contact is set. Continue?`,
      );
      if (!ok) return;
    }

    setSubmitting(true);
    setError(null);
    setResultSummary(null);
    try {
      const { results } = await addHotelsToLaunchedCampaign(campaignId, Array.from(selectedIds));
      setResultSummary(summarizeAddResults(results));
      const hasSuccess = results.some(
        (r) =>
          r.action === 'sent' ||
          r.action === 'reactivated' ||
          r.action === 'skipped_no_contact' ||
          r.action === 'inserted',
      );
      if (hasSuccess) {
        await onSuccess();
        setSelectedIds(new Set());
        void loadHotels();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add hotels');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="add-hotels-modal-title"
      className="fixed inset-0 z-50 m-0 flex w-full max-w-none translate-x-0 translate-y-0 items-center justify-center border-0 bg-transparent p-4 shadow-none open:flex [&::backdrop]:bg-black/50"
      onCancel={(e) => {
        e.preventDefault();
        handleClose();
      }}
    >
      <button
        type="button"
        className={`fixed inset-0 z-0 bg-transparent ${submitting ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        aria-label="Close add hotels dialog"
        disabled={submitting}
        onClick={handleClose}
      />
      <div className="relative z-10 flex w-full max-w-4xl max-h-[90vh] flex-col overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-gray-200/80">
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-3 pr-12">
          <div className="min-w-0">
            <h2 id="add-hotels-modal-title" className="text-base font-semibold text-gray-900">
              Add hotels
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Append hotels to this launched campaign. Initial invitations send automatically when an
              invoicing contact exists.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="absolute right-2 top-2 rounded-lg p-2 text-slate-500 transition-colors hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-action/30 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {error ? (
            <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
              {error}
            </div>
          ) : null}
          {resultSummary ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              {resultSummary}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="relative w-[14rem] max-w-full shrink-0">
              <label htmlFor="add-hotel-search" className="sr-only">
                Search hotels by name
              </label>
              <Search
                className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
                aria-hidden
              />
              <input
                id="add-hotel-search"
                type="search"
                value={hotelSearchQuery}
                onChange={(e) => setHotelSearchQuery(e.target.value)}
                placeholder="Search by name…"
                className="w-full rounded-md border border-gray-300 py-1.5 pl-7 pr-2 text-xs focus:border-action focus:outline-none focus:ring-1 focus:ring-action/40"
              />
            </div>
            <div className="flex shrink-0 gap-3">
              <button
                type="button"
                className="btn-text-action"
                onClick={() =>
                  setSelectedIds((prev) => {
                    const next = new Set(prev);
                    hotelsFiltered.forEach((h) => next.add(h.id));
                    return next;
                  })
                }
              >
                Select all
              </button>
              <button type="button" className="btn-text-action" onClick={() => setSelectedIds(new Set())}>
                Deselect all
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-gray-500">Loading hotels…</div>
          ) : hotels.length === 0 ? (
            <div className="text-sm text-gray-500">
              No hotels available to add (all period hotels are already in this campaign).
            </div>
          ) : hotelsFiltered.length === 0 ? (
            <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600">
              No hotels match your filters.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto rounded-md border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="sticky top-0 bg-gray-50">
                  <tr>
                    <th className="w-10 px-3 py-2" />
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Hotel</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Est. revenue</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Groups</th>
                    <th className="min-w-[10rem] px-3 py-2 text-left text-xs font-medium text-gray-500">
                      Invoicing contact
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {hotelsFiltered.map((h) => (
                    <tr key={h.id} className={!h.invoicing_contact?.trim() ? 'bg-amber-50' : ''}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(h.id)}
                          onChange={() => toggleHotel(h.id)}
                          className="rounded"
                        />
                      </td>
                      <td className="px-3 py-2 text-sm">
                        {h.title}
                        {h.wasCancelled ? (
                          <span className="ml-2 text-xs font-medium text-slate-600">
                            Re-add (was cancelled)
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right text-sm tabular-nums">
                        {h.estimated_revenue > 0 ? formatCurrencyDecimals(h.estimated_revenue) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-sm tabular-nums">{h.groups_count || '—'}</td>
                      <td className="px-3 py-2 text-sm">
                        {h.invoicing_contact?.trim() ? (
                          <span className="text-action">{h.invoicing_contact.trim()}</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {hotelsWithoutEmail.length > 0 ? (
            <button
              type="button"
              onClick={() => setMissingContactFilter((v) => !v)}
              aria-pressed={missingContactFilter}
              className={`text-right text-sm text-amber-800 underline-offset-2 hover:underline ${missingContactFilter ? 'font-semibold' : ''}`}
            >
              {hotelsWithoutEmail.length} hotel(s) without invoicing contact (highlighted)
              {missingContactFilter ? ' · Show all' : ' · Filter list'}
            </button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 px-4 py-3">
          <button type="button" className="btn-secondary" disabled={submitting} onClick={handleClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary disabled:pointer-events-none"
            disabled={submitting || loading || selectedIds.size === 0}
            onClick={() => void handleSubmit()}
          >
            {submitting ? 'Adding…' : 'Add and send'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
